import fluRecommendations from './fluRecommend';
import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import axios from 'axios';
import {getHostIP, getVaccineTypeFromHostname} from "../../networking/Hosts";
import styles from './Chat.module.css';
import ExpandableDropdown from "./ExpandableDropdown";
import MiDebugPanel, {previousMiStates} from './MiDebugPanel';
import ServiceResultCard from './ServiceResultCard';
import ClinicMapPreview from './ClinicMapPreview';
import {parseServiceResult, upsertServiceResult, serviceLocationTool, messageServiceResults, locatedServiceReply} from '../../utils/serviceResults';
import {resolveServiceLocation} from '../../utils/serviceLocation';
import {useNavigate, useSearchParams} from "react-router-dom";
import WebAudioSpeechRecognizer from "../../app/webaudiospeechrecognizer.js";
import WebAudioTTS from "../../app/webaudiotts.js";
import {readStoredHpvLocation, requestHpvLocation} from "../../utils/hpvLocation";
import HpvSubsidyModal from "../../components/HpvSubsidyModal";
import {
    getRemainingHpvConversations,
    HPV_TARGET_CONVERSATIONS
} from "../../data/hpvSubsidy";
import {getRsvRecommendedQuestions, getRsvRecommendedQuestion, completeRsvFreeTextRecommendations} from "./rsvRecommend";

const INTERNAL_ASSISTANT_TAGS = [
    'elicit_2_professional_hook',
    'provide_objective_with_citation',
    'elicit_1_empathy'
];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const internalTagPattern = INTERNAL_ASSISTANT_TAGS.map(escapeRegExp).join('|');
const internalTagRegex = new RegExp(internalTagPattern, 'gi');
const wrappedInternalTagRegex = new RegExp(
    `(?:^|[\\s\\r\\n])(?:[\\[【(（<])?\\s*(?:${internalTagPattern})\\s*(?:[\\]】)）>])?\\s*(?:[:：;；,，.。\\-—–])?`,
    'gi'
);
const xmlInternalTagRegex = new RegExp(`</?\\s*(?:${internalTagPattern})\\s*/?>`, 'gi');

const sanitizeAssistantOutput = (text) => {
    if (!text) return '';
    return String(text)
        .replace(xmlInternalTagRegex, '')
        .replace(wrappedInternalTagRegex, (match) => {
            if (match.includes('\n')) return '\n';
            if (/^\s/.test(match)) return ' ';
            return '';
        })
        .replace(internalTagRegex, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const escapeHtml = (text) => String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const convertMarkdownBold = (html) => html.replace(
    /\*\*([^*\n]+(?:\n(?!\n)[^*\n]+)*)\*\*/g,
    '<strong>$1</strong>'
);

const getHtmlAttribute = (tag, name) => {
    const attrRegex = /([a-zA-Z:-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
    let match;
    while ((match = attrRegex.exec(tag)) !== null) {
        if (match[1].toLowerCase() === name.toLowerCase()) {
            return match[3] ?? match[4] ?? match[5] ?? '';
        }
    }
    return '';
};

const isSafeHref = (href) => /^(https?:\/\/|mailto:|#|\/(?!\/))/i.test(href || '');
const isSafeImageSrc = (src) => /^(https?:\/\/|\/(?!\/))/i.test(src || '');

const preserveAllowedHtml = (text) => {
    const tokens = [];
    const addToken = (html) => {
        const token = `__ALLOWED_HTML_${tokens.length}__`;
        tokens.push({token, html});
        return token;
    };

    const protectedText = String(text || '')
        .replace(/<\s*img\b[^>]*>/gi, (tag) => {
            const src = getHtmlAttribute(tag, 'src');
            if (!isSafeImageSrc(src)) return '';
            const alt = getHtmlAttribute(tag, 'alt');
            return addToken(
                `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" />`
            );
        })
        .replace(/<\s*a\b[^>]*>/gi, (tag) => {
            const href = getHtmlAttribute(tag, 'href');
            if (!isSafeHref(href)) return '';
            return addToken(
                `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`
            );
        })
        .replace(/<\s*\/\s*a\s*>/gi, () => addToken('</a>'))
        .replace(/<\s*br\s*\/?\s*>/gi, () => addToken('<br />'));

    return {protectedText, tokens};
};

const restoreAllowedHtml = (html, tokens) => tokens.reduce(
    (result, {token, html: safeHtml}) => result.split(token).join(safeHtml),
    html
);

const looksLikeClinicLocationRequest = (text) => {
    const value = String(text || '');
    const hasNearIntent = ['附近', '最近', '就近', '离我', '距离'].some((keyword) => value.includes(keyword));
    const hasClinicIntent = ['接种点', '接种门诊', '门诊', '接种单位', '哪里打', '去哪打', '去哪里打'].some((keyword) => value.includes(keyword));
    return hasNearIntent && hasClinicIntent;
};

const ClinicRecommendationCard = ({data, onRetry}) => {
    if (!data) return null;
    const clinics = Array.isArray(data.clinics) ? data.clinics : [];
    const status = data.status || 'error';

    if (status !== 'ok') {
        return (
            <div className={`${styles.clinic_card} ${styles.clinic_card_notice}`}>
                <div className={styles.clinic_card_title}>附近接种点</div>
                <div className={styles.clinic_card_message}>{data.message || '暂时无法查询附近接种点。'}</div>
                {onRetry && data.vaccine_type === 'RSV' && status !== 'locating' && (
                    <button type="button" onClick={onRetry}>重新定位并查询</button>
                )}
                {data.accuracy_warning && (
                    <div className={styles.clinic_card_message}>{data.accuracy_warning}</div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.clinic_card}>
            <div className={styles.clinic_card_title}>{data.vaccine_type === 'RSV' ? (data.query_type === 'pilot_status' ? '附近 RCT 试点' : '附近 RSV 接种机构') : '附近 HPV 接种点'}</div>
            <div className={styles.clinic_card_message}>{data.message}</div>
            {data.accuracy_warning && (
                <div className={styles.clinic_card_message}>{data.accuracy_warning}</div>
            )}
            {data.notice && <div className={styles.clinic_card_message}>{data.notice}</div>}
            <div className={styles.clinic_list}>
                {clinics.map((clinic, index) => (
                    <div className={styles.clinic_item} key={`${clinic.name}-${index}`}>
                        <ClinicMapPreview clinic={clinic}/>
                        <div className={styles.clinic_info}>
                            <div className={styles.clinic_name}>
                                {clinic.name}
                                <span className={styles.clinic_distance}>{data.vaccine_type === 'RSV' ? '直线约 ' : '约 '}{clinic.distance_km} km</span>
                            </div>
                            <div className={styles.clinic_meta}>{clinic.address}</div>
                            <div className={styles.clinic_meta}>电话：{clinic.phone || '未登记'}</div>
                            {clinic.eligibility_restriction && <div className={styles.clinic_meta}>服务限制：{clinic.eligibility_restriction}</div>}
                            {clinic.pilot_status && <div className={styles.clinic_meta}>试点状态：{clinic.pilot_status}</div>}
                            {clinic.source_as_of && <div className={styles.clinic_meta}>资料截至：{clinic.source_as_of === 'unknown' ? '未提供' : clinic.source_as_of}</div>}
                            {clinic.opening_hours && <div className={styles.clinic_meta}>开诊：{clinic.opening_hours}</div>}
                            <div className={styles.clinic_actions}>
                                <a href={clinic.map_url} target="_blank" rel="noopener noreferrer">查看地图</a>
                                <a href={clinic.navigation_url} target="_blank" rel="noopener noreferrer">导航前往</a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// 将Avatar组件移到组件外部，避免重复创建
const Avatar = React.memo(({role, gender, chatType}) => {
    // 根据chatType和role选择头像
    let avatarUrl = '';
    if (role === 'gpt') {
        // GPT头像始终使用robot
        avatarUrl = '/robot.png';
    } else {
        // 用户头像：如果是肿瘤/癌症类型，使用老人头像；否则使用boy/girl
        if (chatType === 'CRC' || chatType === '肿瘤' || chatType === '癌症') {
            avatarUrl = gender === 1 ? '/grandfather.png' : '/grandmother.png';
        } else {
            avatarUrl = gender === 1 ? "/boy.png" : "/girl.png";
        }
    }
    
    // 根据chatType判断显示什么专家
    let avatarName = '';
    if (role === 'gpt') {
        // 如果是CRC，显示癌症专家；流感和HPV等其他类型显示疫苗专家
        if (chatType === 'CRC' || chatType === '肿瘤' || chatType === '癌症') {
            avatarName = '家庭医生';
        } else {
            // 流感和HPV等疫苗类型显示疫苗专家
            avatarName = '疫苗专家';
        }
    }
    
    return <div className={styles.avatar_container}><img src={avatarUrl} alt={role} className={styles.avatar}/>
        <div className={styles.nickname}>{avatarName}</div>
    </div>;
});

const getHpvInitialRecommendedQuestions = (initialAttitude) => {
    if (initialAttitude === 'A') {
        return [
            {"question": "HPV疫苗的价格是多少？", "index": 68},
            {"question": "有哪些HPV疫苗可选？", "index": 73},
            {"question": "怎么预约HPV疫苗？", "index": 119}
        ];
    }
    if (initialAttitude === 'B') {
        return [
            {"question": "HPV疫苗有什么用？", "index": 11},
            {"question": "HPV疫苗有效吗？能预防宫颈癌吗？", "index": 12},
            {"question": "HPV疫苗安全吗？", "index": 58}
        ];
    }
    if (initialAttitude === 'C') {
        return [
            {"question": "HPV疫苗安全吗？", "index": 58},
            {"question": "全球HPV疫苗的接种情况如何？", "index": 90},
            {"question": "HPV疫苗的常见误解有哪些？", "index": 113}
        ];
    }
    return [
        {"question": "HPV疫苗有什么用？", "index": 11},
        {"question": "HPV疫苗安全吗？", "index": 58},
        {"question": "如何预约HPV疫苗接种？", "index": 95}
    ];
};

// 根据chatType获取对应的推荐问题
const getDefaultRecommendedQuestions = (type, initialAttitude) => {
    if (type === 'CRC' || type === '肿瘤' || type === '癌症' || type === '肠癌') {
        return [
            {"question": "什么是肠癌？", "index": 1},
            {"question": "为什么大部分肠癌发现即是晚期？", "index": 9},
            {"question": "结直肠癌有哪些常见症状？", "index": 15}
        ];
    } else if (type === 'HPV') {
        return getHpvInitialRecommendedQuestions(initialAttitude);
    } else if (type === 'RSV') {
        return getRsvRecommendedQuestions([]);
    } else {
        return fluRecommendations.getQuestions([]);
    }
};

// 根据chatType获取对应的欢迎消息
const getHpvOpeningMessage = (initialAttitude) => {
    if (initialAttitude === 'A') {
        return "您好！很高兴看到您想接种 HPV 疫苗，主动防护宫颈癌。\n关于疫苗接种，您想了解哪些信息呢？比如预约流程、疫苗选择、疫苗价格？\n您可以直接提问，也可以从下面问题中选择。";
    }
    if (initialAttitude === 'B') {
        return "您好！我非常理解您的感受。疫苗接种关乎健康，确实需要多了解再决定。很多人接种前都会有些犹豫。\n关于HPV 疫苗，您纠结的是哪方面呢？比如疫苗是否安全、有效？\n您可以直接提问，也可以从下面问题中选择。";
    }
    if (initialAttitude === 'C') {
        return "您好！我非常理解您的想法，接种疫苗是您的权利，您可以自由决定接不接种。\n您能跟我说下，不想打的原因吗？比如觉得不安全、周围没人打？\n您可以直接提问，也可以从下面问题中选择。";
    }
    return "您好，欢迎使用HPV疫苗智能问答助手，复旦大学和疾控中心合作，随时为您提供专业解答！关于HPV疫苗，您有什么要咨询的吗？";
};

const getWelcomeMessage = (type, initialAttitude) => {
    if (type === 'CRC' || type === '肿瘤' || type === '癌症' || type === '肠癌') {
        return "您好，欢迎使用肠癌筛查智能问答助手，疾控中心和复旦大学合作，随时为您提供专业解答！关于肠癌筛查，您有什么要咨询的吗？\n请您至少问10个问题，快来提问吧！\n如果您还没想好问题，建议点击右上角的“必备知识”。";
    } else if (type === 'HPV') {
        return getHpvOpeningMessage(initialAttitude);
    } else if (type === 'RSV') {
        return "您好，欢迎使用RSV预防智能问答助手，疾控中心和复旦大学合作，随时为您提供专业解答！关于呼吸道合胞病毒感染、疫苗或单克隆抗体，您有什么想了解的吗？\n请您至少问10个问题，快来提问吧！";
    } else {
        // 默认流感疫苗
        return "您好，欢迎使用流感疫苗智能问答助手，疾控中心和复旦大学合作，随时为您提供专业解答！关于流感疫苗，您有什么要咨询的吗？\n请您至少问10个问题，快来提问吧！\n如果您还没想好问题，建议点击右上角的“必备知识”。";
    }
};

const GptChat = () => {
    const chatType = getVaccineTypeFromHostname();
    const isStageProject = ['RSV', 'FLU', '流感'].includes(chatType);
    const stageRecommendations = chatType === 'RSV' ? {
        getQuestion: getRsvRecommendedQuestion, getQuestions: getRsvRecommendedQuestions,
        completeFreeText: completeRsvFreeTextRecommendations
    } : fluRecommendations;
    console.log(chatType);
    const [searchParams] = useSearchParams();
    const debugMode = searchParams.get('debug') === 'true';
    const currentHostIP = getHostIP();
    const mobileMode = searchParams.get('mobile') === 'true'; // 从URL获取mobile参数，控制是否使用移动端模式
    const initialAttitude = searchParams.get('initial_attitude');
    
    const [message, setMessage] = useState('');
    const [inputHeight, setInputHeight] = useState(40); // 动态输入框高度
    const [chatLog, setChatLog] = useState([]);
    const lastAssistantIndex = useMemo(() => {
        // Older mobile webviews do not implement Array.prototype.findLastIndex.
        for (let index = chatLog.length - 1; index >= 0; index -= 1) {
            if (chatLog[index].role === 'gpt') return index;
        }
        return -1;
    }, [chatLog]);
    const previousMiDebugStates = useMemo(() => previousMiStates(chatLog), [chatLog]);
    const [isLoading, setIsLoading] = useState(false);
    const recommendationInFlightRef = useRef(false);
    const recommendationRequestRef = useRef(0);
    const streamInFlightRef = useRef(false);
    const [displayedMessage, setDisplayedMessage] = useState("");

    const [recommendedQuestions, setRecommendedQuestions] = useState(getDefaultRecommendedQuestions(chatType, initialAttitude));
    const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false); // 新增状态变量
    const endOfChatRef = useRef(null); // 用于滚动到聊天记录底部
    const [isInit, setIsInit] = useState(true);
    const [historyLength, setHistoryLength] = useState(0);
    const navigate = useNavigate(); // 使用 useNavigate 进行页面跳转
    const initializedHpvChatsRef = useRef(new Set());

    // 获取 'name' 参数的值
    const gender = searchParams.get('gender') ? searchParams.get('gender') : 0;


    const [conversationCount, setConversationCount] = useState(0);
    const [hpvCompletedRounds, setHpvCompletedRounds] = useState(0);
    const [showHpvSubsidyModal, setShowHpvSubsidyModal] = useState(false);
    const [toolCalls, setToolCalls] = useState({}); // 存储工具调用信息
    const [expandedTools, setExpandedTools] = useState({}); // 存储展开状态
    
    // 语音识别相关状态
    const [isRecording, setIsRecording] = useState(false);
    const [recognizedText, setRecognizedText] = useState('');
    const [recordingStatus, setRecordingStatus] = useState(''); // 'connecting', 'recording', ''
    const webAudioSpeechRecognizerRef = useRef(null);
    const [tencentCredentials, setTencentCredentials] = useState(null);
    const recordingStartTimeRef = useRef(null); // 记录录音开始时间

    // TTS相关状态
    const [isPlaying, setIsPlaying] = useState(false);
    const [isSynthesizing, setIsSynthesizing] = useState(false); // TTS合成中
    const [currentPlayingMessage, setCurrentPlayingMessage] = useState(null);
    const [playbackProgress, setPlaybackProgress] = useState(0); // 播放进度 0-100
    const [currentTime, setCurrentTime] = useState(0); // 当前播放时间（秒）
    const [totalDuration, setTotalDuration] = useState(0); // 总时长（秒）
    const webAudioTTSRef = useRef(null);
    
    // 自动朗读设置，从 localStorage 读取，默认开启
    const [autoPlayVoice, setAutoPlayVoice] = useState(() => {
        const saved = localStorage.getItem('autoPlayVoice');
        return saved !== null ? saved === 'true' : true; // 默认开启
    });

    // 移动端相关状态
    const [inputMode, setInputMode] = useState('voice'); // 'text' 或 'voice'
    const [touchStartY, setTouchStartY] = useState(0);
    const [currentTouchY, setCurrentTouchY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [showVoiceOverlay, setShowVoiceOverlay] = useState(false);
    const [isWaitingForPermission, setIsWaitingForPermission] = useState(false); // 等待权限状态
    const voiceButtonRef = useRef(null);
    
    // 语音识别使用指南
    const [showVoiceGuide, setShowVoiceGuide] = useState(false);
    const [currentGuideStep, setCurrentGuideStep] = useState(1); // 当前步骤 (1, 2, 3, 4)
    const [isWechatBrowser, setIsWechatBrowser] = useState(false);
    const [isIOSWechat, setIsIOSWechat] = useState(false);
    const [isAndroidWechat, setIsAndroidWechat] = useState(false);
    const [isDesktopBrowser, setIsDesktopBrowser] = useState(false);
    const [forceDesktopMode, setForceDesktopMode] = useState(false); // 强制桌面模式，禁用按住说话
    const [highlightLastBubble, setHighlightLastBubble] = useState(false); // 高亮最后一个assistant气泡
    const hasCheckedGuide = useRef(false); // 标记是否已检查过引导，防止重复弹窗


    // 获取腾讯云临时密钥
    const getTencentToken = async () => {
        try {
            const response = await axios.get(currentHostIP + '/api/tencent/token', {
                withCredentials: true
            });
            if (response.data.success) {
                const tokenData = {
                    ...response.data.data,
                    appid: 1328869042
                };
                setTencentCredentials(tokenData);
                return tokenData;
            }
        } catch (error) {
            console.error('获取腾讯云token失败:', error);
            addErrorBubble('获取语音识别权限失败，请重试。');
        }
        return null;
    };

    useEffect(() => {
        console.log(searchParams.get('mobile'));
        const ua = navigator.userAgent.toLowerCase();
        const isWechat = /micromessenger|wechat/i.test(ua);
        const isMobile = /android|webos|iphone|ipad|ipod|ios|blackberry|iemobile|opera mini/i.test(ua);
        const isIOS = /iphone|ipad|ipod/i.test(ua);
        const isAndroid = /android/i.test(ua);
        const isDesktop = !isMobile;

        setIsWechatBrowser(isWechat);
        setIsIOSWechat(isWechat && isIOS);
        setIsAndroidWechat(isWechat && isAndroid && !isIOS); // 确保Android不包括iOS
        setIsDesktopBrowser(isDesktop);

        const shouldForceDesktop = searchParams.get('mobile') === 'false' || isWechat;
        setForceDesktopMode(shouldForceDesktop);
        setInputMode(shouldForceDesktop ? 'text' : 'voice');
    }, [searchParams]);

    // 检测浏览器环境并显示语音识别使用指南（只在组件首次加载时执行一次）
    useEffect(() => {
        // 如果已经检查过，直接返回，避免重复弹窗
        if (hasCheckedGuide.current) return;

        // 检查credential，判断是否已经显示过引导
        const hasShownVoiceGuide = localStorage.getItem('hasShownVoiceGuide');
        if (hasShownVoiceGuide) {
            hasCheckedGuide.current = true;
            return;
        }
        setShowVoiceGuide(true);
        // 记录到localStorage，确保只显示一次
        localStorage.setItem('hasShownVoiceGuide', 'true');
        // 标记已检查过
        hasCheckedGuide.current = true;
    }, [searchParams]);
    
    useEffect(() => {
        const checkCredential = async () => {
            const response = await axios.get(currentHostIP + '/api/checkCredential')
            if (!response.data.message) {
                navigate('/')
            }
        }
        const loadChatLogs = async () => {
            const loadedChatLogs = await loadChatFromServer();
            // A failed request is not an empty conversation. Preserve the error
            // and do not initialize/save a new opening over existing history.
            if (loadedChatLogs === null) return;
            if (loadedChatLogs && loadedChatLogs.length > 0) {
                setHistoryLength(loadedChatLogs.length);
                setChatLog(loadedChatLogs);
                if (isStageProject) {
                    const lastUser = [...loadedChatLogs].reverse().find(m => m.role === 'user');
                    if (lastUser) {
                        const selected = lastUser.inputSource === 'recommendation'
                            ? stageRecommendations.getQuestion({index: lastUser.recommendationIndex, question: lastUser.content}) : null;
                        fetchRecommendedQuestions(loadedChatLogs, selected);
                    }
                }
            } else {
                const openingMessage = {
                    role: 'gpt',
                    content: getWelcomeMessage(chatType, initialAttitude)
                };
                setChatLog([openingMessage]);

                // HPV 有三种基于初始态度的开场内容。新会话创建时立即将对应
                // 开场白写入聊天记录，确保记录中的第一条消息不是用户输入。
                if (chatType === 'HPV') {
                    const initializationKey = `${chatType}:${initialAttitude || 'default'}`;
                    if (!initializedHpvChatsRef.current.has(initializationKey)) {
                        initializedHpvChatsRef.current.add(initializationKey);
                        await saveChatToServer([openingMessage]);
                    }
                    setHistoryLength(1);
                } else {
                    setHistoryLength(0);
                }
            }
            console.log(loadedChatLogs)
        };
        checkCredential().then(() => {
            loadChatLogs();
            // 预先获取腾讯云token
            getTencentToken();
        });

    }, [chatType, navigate, currentHostIP, initialAttitude]);

    // 全局触摸监听器，确保在权限对话框期间松手也能被检测到
    useEffect(() => {
        const handleGlobalTouchEnd = (e) => {
            // 只有在移动端语音模式且正在等待权限或录音时才处理
            if (mobileMode && inputMode === 'voice' && (isWaitingForPermission || isRecording)) {
                // 模拟触摸结束事件
                handleTouchEnd(e);
            }
        };

        // 添加全局触摸监听器
        document.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });

        return () => {
            document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [mobileMode, inputMode, isWaitingForPermission, isRecording]);

    // 组件卸载时清理资源
    useEffect(() => {
        return () => {
            // 组件卸载时确保释放麦克风资源
            if (webAudioSpeechRecognizerRef.current) {
                webAudioSpeechRecognizerRef.current.stop();
                webAudioSpeechRecognizerRef.current.destroyStream();
                webAudioSpeechRecognizerRef.current = null;
            }
        };
    }, []);

    // 当chatType变化时，更新推荐问题和页面标题
    useEffect(() => {
        setRecommendedQuestions(getDefaultRecommendedQuestions(chatType, initialAttitude));
        
        // 根据chatType更新页面标题
        let pageTitle = '';
        if (chatType === 'CRC' || chatType === '肿瘤' || chatType === '癌症' || chatType === '肠癌') {
            pageTitle = '肠癌筛查智能问答助手';
        } else if (chatType === 'HPV') {
            pageTitle = 'HPV疫苗智能问答助手';
        } else if (chatType === 'RSV') {
            pageTitle = 'RSV预防智能问答助手';
        } else {
            // 默认流感疫苗
            pageTitle = '流感疫苗智能问答助手';
        }
        document.title = pageTitle;
    }, [chatType, initialAttitude]);

    const refreshHpvCompletedRounds = useCallback(async () => {
        if (chatType !== 'HPV') return;

        try {
            const response = await axios.get(`${currentHostIP}/api/getConversationCount`, {
                params: {vaccine_type: 'HPV'}
            });
            setHpvCompletedRounds(Math.floor((response.data.count || 0) / 2));
        } catch (error) {
            console.error('获取HPV累计问答次数失败:', error);
        }
    }, [chatType, currentHostIP]);

    useEffect(() => {
        refreshHpvCompletedRounds();
    }, [refreshHpvCompletedRounds]);

    const saveChatToServer = async (chatLog) => {
        try {
            await axios.post(`${currentHostIP}/api/saveChat`, {
                chat_log: chatLog.map(({miState, ...message}) => message),
                vaccine_type: chatType,
            });
            await refreshHpvCompletedRounds();
        } catch (error) {
            console.error('Error saving chat:', error);
            if (error.response?.status === 401) {
                addErrorBubble('无法保存聊天记录，请尝试启用第三方cookie。');
            } else {
                addErrorBubble('保存聊天记录时发生错误。');
            }
        }
    };

    const loadChatFromServer = async () => {
        try {
            const params = {
                vaccine_type: chatType,
            };
            const response = await axios.get(`${currentHostIP}/api/loadChat`, {params});
            const payload = response.data.chat_logs;
            // Current API returns an array; older deployments returned JSON text.
            const messages = typeof payload === 'string' ? JSON.parse(payload) : payload;
            if (!Array.isArray(messages) || messages.some(message =>
                !message || typeof message !== 'object' || Array.isArray(message)
            )) {
                throw new TypeError('聊天记录格式错误：期望消息对象数组');
            }
            return messages;
        } catch (error) {
            console.error('Error loading chat:', error);
            if (error.response?.status === 401) {
                addErrorBubble('无法加载聊天记录，请尝试启用第三方cookie。');
            } else {
                addErrorBubble('加载聊天记录时出错，请重试。');
            }
            return null;
        }
    };

    const addErrorBubble = (errorMessage) => {
        const errorBubble = {role: 'system', content: errorMessage};
        setChatLog(prevChatLog => [...prevChatLog, errorBubble]);
    };


    // 工具调用组件
    const ToolCallComponent = ({toolCall, toolOutput, uuid}) => {
        const isExpanded = expandedTools[uuid] || false;
        const isSearching = !toolOutput; // 没有输出表示正在搜索
        const toggleExpanded = () => {
            setExpandedTools(prev => ({
                ...prev,
                [uuid]: !prev[uuid]
            }));
        };

        // 智能显示工具调用名称
        const getDisplayName = () => {
            if (isSearching) {
                return '正在搜索知识库...';
            }
            
            // 如果是知识库查询工具，显示更友好的名称
            if (toolCall.name === 'query_knowledge_base') {
                try {
                    // 尝试解析输出结果，计算查询到的结果数量
                    if (toolOutput) {
                        const outputData = JSON.parse(toolOutput);
                        if (Array.isArray(outputData)) {
                            return `在知识库中查询到了 ${outputData.length} 个结果`;
                        } else if (outputData && typeof outputData === 'object') {
                            // 如果输出是对象，尝试找到结果数组
                            const resultKeys = Object.keys(outputData).filter(key => 
                                Array.isArray(outputData[key]) && outputData[key].length > 0
                            );
                            if (resultKeys.length > 0) {
                                const totalResults = resultKeys.reduce((sum, key) => sum + outputData[key].length, 0);
                                return `在知识库中查询到了 ${totalResults} 个结果`;
                            }
                        }
                    }
                    return '知识库查询完成';
                } catch (e) {
                    return '知识库查询完成';
                }
            }

            if (toolCall.name === 'request_device_location') return isSearching ? '正在定位并查询附近机构…' : '附近机构查询结果';
            if (toolCall.name === 'query_rsv_service_data') return isSearching ? '正在查询服务登记库…' : '服务登记查询结果';
            if (toolCall.name === 'find_nearest_hpv_clinic') {
                return isSearching ? '正在查询附近接种点...' : '附近接种点查询完成';
            }
            
            return toolCall.name;
        };

        return (
            <div className={`${styles.tool_call_container} ${isSearching ? styles.tool_call_searching : ''}`}>
                <div className={styles.tool_call_header} onClick={toggleExpanded}>
                    <span className={styles.tool_call_icon}>
                        {isSearching ? '🔍' : (toolCall.name === 'query_knowledge_base' ? '📚' : '🔧')}
                    </span>
                    <span className={styles.tool_call_name}>
                        {getDisplayName()}
                    </span>
                    <span className={styles.tool_call_toggle}>
                        {isExpanded ? '▼' : '▶'}
                    </span>
                </div>
                {isExpanded && (
                    <div className={styles.tool_call_content}>
                        <div className={styles.tool_call_args}>
                            <strong>查询参数:</strong>
                            <pre>{JSON.stringify(toolCall.arguments, null, 2)}</pre>
                        </div>
                        {toolOutput && (
                            <div className={styles.tool_call_output}>
                                <strong>查询结果:</strong>
                                <div className={styles.tool_output_content}>
                                    {toolOutput}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };
    const handleSendMessageStream = useCallback(async (message, selectedQuestion = null) => {
        // 不允许发送空消息
        if (streamInFlightRef.current || recommendationInFlightRef.current || !message || message.trim() === '') {
            return;
        }
        
        streamInFlightRef.current = true;
        // 保存录音状态，以便回答完成后恢复（使用ref检查，避免闭包问题）
        const wasRecording = webAudioSpeechRecognizerRef.current !== null;
        
        // 如果正在录音，先停止录音并清空识别结果
        if (wasRecording) {
            // 直接停止录音并释放资源
            if (webAudioSpeechRecognizerRef.current) {
                webAudioSpeechRecognizerRef.current.stop();
                webAudioSpeechRecognizerRef.current.destroyStream();
                webAudioSpeechRecognizerRef.current = null;
            }
            setIsRecording(false);
            setRecordingStatus('');
            setRecognizedText('');
        }
        
        const isClinicLocationRequest = chatType === 'HPV' && looksLikeClinicLocationRequest(message);
        let storedHpvLocation = isClinicLocationRequest ? null : (chatType === 'HPV' ? readStoredHpvLocation() : null);
        if (isClinicLocationRequest) {
            try {
                storedHpvLocation = await requestHpvLocation({maximumAge: 0});
            } catch (error) {
                streamInFlightRef.current = false;
                console.error('定位授权失败:', error);
                setConversationCount(conversationCount + 1);
                setIsInit(false);
                setMessage('');
                setChatLog([
                    ...chatLog,
                    {role: 'user', content: message},
                    {role: 'gpt', content: '要帮您推荐附近最近的 HPV 疫苗接种点，需要先获取您的当前位置。请允许浏览器定位后再试。'}
                ]);
                return;
            }
        }
        const payload = {
            message: message,
            history_messages: chatLog.slice(-5).map(({miState, ...message}) => message),
            vaccine_type: chatType
        };
        if (storedHpvLocation) {
            payload.user_location = {
                latitude: storedHpvLocation.latitude,
                longitude: storedHpvLocation.longitude,
                accuracy: storedHpvLocation.accuracy,
                coordinate_system: storedHpvLocation.coordinate_system || 'wgs84'
            };
        }
        if (isClinicLocationRequest) {
            console.log('[HPV clinic] stream request', {
                host: currentHostIP,
                hasLocation: Boolean(payload.user_location),
                locationSource: storedHpvLocation?.source || 'browser',
                accuracy: storedHpvLocation?.accuracy,
                coordinateSystem: storedHpvLocation?.coordinate_system || 'wgs84'
            });
        }
        setConversationCount(conversationCount + 1);
        setIsInit(false);
        setIsLoading(true);
        const userMessage = {role: 'user', content: message, ...(isStageProject ? {
            inputSource: selectedQuestion ? 'recommendation' : 'free_text',
            recommendationIndex: selectedQuestion?.index ?? null
        } : {})};
        recommendationRequestRef.current += 1;
        const updatedChatLog = [...chatLog, userMessage];
        setChatLog(updatedChatLog);

        const pendingClientTools = [];
        let streamActive = true;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时

        try {
            const response = await fetch(currentHostIP + `/api/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': '123456', // 你的安全密钥
                },
                body: JSON.stringify(payload),
                signal: controller.signal, // 关联超时控制
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let rawTextContent = '';
            let currentTextContent = '';
            let currentToolCalls = {};
            let currentClinicRecommendation = null;
            let currentMiState = null;
            let currentServiceResults = [];
            const clientToolIds = new Set();

            const gptMessage = {role: 'gpt', content: "", toolCalls: []};
            const newChatLog = [...updatedChatLog, gptMessage];
            setChatLog(newChatLog);
            setMessage('');
            const publishCurrentReply = () => setChatLog([...newChatLog.slice(0, -1), {
                role: 'gpt', content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                toolCalls: Object.values(currentToolCalls),
                clinicRecommendation: currentClinicRecommendation,
                miState: currentMiState, serviceResults: currentServiceResults
            }]);

            while (true) {
                const {value, done} = await reader.read();
                buffer += done ? decoder.decode() : decoder.decode(value, {stream: true});
                // 连接结束时也处理没有换行符的最后一条事件。
                if (done && buffer.trim()) buffer += '\n';

                // 处理完整的JSON对象
                let newlineIndex = buffer.indexOf('\n');
                while (newlineIndex !== -1) {
                    const line = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 1);
                    newlineIndex = buffer.indexOf('\n');

                    if (line) {
                        try {
                            // 处理SSE格式的数据包
                            let jsonString = line;

                            // 跳过SSE注释行
                            if (line.startsWith(':')) {
                                continue;
                            }

                            // 处理data:前缀
                            if (line.startsWith('data: ')) {
                                jsonString = line.substring(6); // 移除 "data: " 前缀
                            } else if (line.startsWith('data:')) {
                                jsonString = line.substring(5); // 移除 "data:" 前缀
                            }

                            // 跳过空数据
                            if (!jsonString.trim()) {
                                continue;
                            }

                            const jsonData = JSON.parse(jsonString);
                            console.log('Received JSON:', jsonData);

                            switch (jsonData.type) {
                                case 'tool_call':
                                    // 处理工具调用
                                    const toolCallData = jsonData.tool_call || jsonData.content;
                                    const toolUuid = jsonData.uuid || `tool_${Date.now()}_${Math.random()}`;

                                    if (clientToolIds.has(toolUuid)) break;
                                    currentToolCalls[toolUuid] = {
                                        toolCall: toolCallData,
                                        toolOutput: null,
                                        uuid: toolUuid
                                    };
                                    setToolCalls(prev => ({
                                        ...prev,
                                        [toolUuid]: {
                                            toolCall: toolCallData,
                                            toolOutput: null,
                                            uuid: toolUuid
                                        }
                                    }));

                                    // 立即更新聊天记录以显示工具调用
                                    const toolCallMessage = {
                                        role: 'gpt',
                                        content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                                        toolCalls: Object.values(currentToolCalls),
                                        clinicRecommendation: currentClinicRecommendation,
                                        miState: currentMiState,
                                        serviceResults: currentServiceResults
                                    };
                                    const updatedChatLogWithToolCall = [...newChatLog.slice(0, -1), toolCallMessage];
                                    setChatLog(updatedChatLogWithToolCall);

                                    const locationTool = serviceLocationTool(toolCallData, message);
                                    if (locationTool && chatType === 'RSV' && !clientToolIds.has(toolUuid)) {
                                        clientToolIds.add(toolUuid);
                                        currentClinicRecommendation = {
                                            vaccine_type: 'RSV', status: 'locating', clinics: [],
                                            message: '正在获取设备位置并查询附近登记机构…'
                                        };
                                        publishCurrentReply();
                                        const pending = resolveServiceLocation(locationTool, currentHostIP)
                                            .catch(error => ({
                                                type: 'clinic_recommendation', vaccine_type: 'RSV',
                                                query_type: locationTool.arguments?.query_type,
                                                status: 'error', clinics: [], message: error.message
                                            }))
                                            .then(result => {
                                                if (!streamActive) return;
                                                currentClinicRecommendation = result;
                                                currentToolCalls[toolUuid].toolOutput = result.message;
                                                publishCurrentReply();
                                            });
                                        pendingClientTools.push(pending);
                                    }

                                    console.log('工具调用开始:', toolCallData, 'UUID:', toolUuid);
                                    console.log('当前所有工具调用UUID:', Object.keys(currentToolCalls));
                                    break;

                                case 'tool_output':
                                    // 处理工具输出
                                    const outputUuid = jsonData.uuid;
                                    const serviceResult = parseServiceResult(jsonData.content);
                                    if (serviceResult) {
                                        currentServiceResults = upsertServiceResult(currentServiceResults, serviceResult);
                                        publishCurrentReply();
                                    }
                                    
                                    if (!outputUuid) {
                                        console.warn('工具输出缺少UUID，无法匹配对应的工具调用');
                                        break;
                                    }
                                    
                                    if (currentToolCalls[outputUuid]) {
                                        currentToolCalls[outputUuid].toolOutput = jsonData.content;
                                        setToolCalls(prev => ({
                                            ...prev,
                                            [outputUuid]: {
                                                ...prev[outputUuid],
                                                toolOutput: jsonData.content
                                            }
                                        }));

                                        // 更新聊天记录以显示工具输出
                                        const toolOutputMessage = {
                                            role: 'gpt',
                                            content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                                            toolCalls: Object.values(currentToolCalls),
                                            clinicRecommendation: currentClinicRecommendation,
                                            miState: currentMiState,
                                            serviceResults: currentServiceResults
                                        };
                                        const updatedChatLogWithToolOutput = [...newChatLog.slice(0, -1), toolOutputMessage];
                                        setChatLog(updatedChatLogWithToolOutput);

                                        console.log('工具调用完成:', outputUuid, jsonData.content);
                                    } else {
                                        console.warn('未找到对应的工具调用UUID:', outputUuid, '当前工具调用:', Object.keys(currentToolCalls));
                                    }
                                    break;

                                case 'text':
                                    // 处理文本内容
                                    rawTextContent += jsonData.content || '';
                                    currentTextContent = sanitizeAssistantOutput(rawTextContent);
                                    setDisplayedMessage(currentTextContent);
                                    const updatedGptMessage = {
                                        role: jsonData.role === 'assistant' ? 'gpt' : jsonData.role || 'gpt',
                                        content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                                        toolCalls: Object.values(currentToolCalls),
                                        clinicRecommendation: currentClinicRecommendation,
                                        miState: currentMiState,
                                        serviceResults: currentServiceResults
                                    };
                                    console.log('处理文本消息:', updatedGptMessage);
                                    const updatedChatLogWithMessage = [...newChatLog.slice(0, -1), updatedGptMessage];
                                    setChatLog(updatedChatLogWithMessage);
                                    break;

                                case 'clinic_recommendation':
                                    currentClinicRecommendation = jsonData.content || null;
                                    const updatedGptMessageWithClinic = {
                                        role: jsonData.role === 'assistant' ? 'gpt' : 'gpt',
                                        content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                                        toolCalls: Object.values(currentToolCalls),
                                        clinicRecommendation: currentClinicRecommendation,
                                        miState: currentMiState,
                                        serviceResults: currentServiceResults
                                    };
                                    setChatLog([...newChatLog.slice(0, -1), updatedGptMessageWithClinic]);
                                    break;

                                case 'service_result':
                                    if (jsonData.content?.type === 'rsv_service_data') {
                                        currentServiceResults = upsertServiceResult(currentServiceResults, jsonData.content);
                                        publishCurrentReply();
                                    }
                                    break;

                                case 'mi_state':
                                    currentMiState = jsonData.content || null;
                                    setChatLog([...newChatLog.slice(0, -1), {
                                        role: 'gpt',
                                        content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                                        toolCalls: Object.values(currentToolCalls),
                                        clinicRecommendation: currentClinicRecommendation,
                                        miState: currentMiState,
                                        serviceResults: currentServiceResults
                                    }]);
                                    break;

                                case 'complete':
                                case 'end':
                                    // 对话完成
                                    console.log('对话完成');
                                    break;
                            }
                        } catch (e) {
                            console.error('解析JSON失败:', e, 'Line:', line);
                        }
                    }

                }
                if (done) break;
            }

            clearTimeout(timeoutId); // 清除流式超时；定位请求有独立超时
            await Promise.all(pendingClientTools);
            setIsLoading(false);

            // 保存最终的消息
            const finalMessage = {
                role: 'gpt', // 统一使用 'gpt' 角色
                content: currentClinicRecommendation?.vaccine_type === 'RSV' ? locatedServiceReply(currentClinicRecommendation) : currentTextContent,
                toolCalls: Object.values(currentToolCalls),
                clinicRecommendation: currentClinicRecommendation,
                miState: currentMiState,
                serviceResults: currentServiceResults
            };

            saveChatToServer([
                userMessage,
                finalMessage
            ]);

            const finalChatLog = [...newChatLog.slice(0, -1), finalMessage];
            setChatLog(finalChatLog); // 更新聊天记录
            fetchRecommendedQuestions(finalChatLog, selectedQuestion);
            
            // 如果自动朗读开启，自动播放最后一条GPT消息
            if ((autoPlayVoice && isDesktopBrowser) && finalMessage.content && finalMessage.content.trim()) {
                // 延迟一小段时间，确保消息已渲染和状态已更新
                setTimeout(() => {
                    const messageIndex = finalChatLog.length - 1;
                    startTTSPlayback(finalMessage.content, messageIndex);
                }, 1500);
            }
        } catch (e) {
            if (e.name === 'AbortError') {
                console.error('请求超时');
                fetchRecommendedQuestions(updatedChatLog, selectedQuestion);
            } else {
                console.error(e);
            }
            const errorMessage = e.name === 'AbortError'
                ? '请求超时了，请稍后再试。'
                : '查询时遇到网络问题，请稍后再试。';
            setChatLog([...updatedChatLog, {role: 'gpt', content: errorMessage}]);
            setIsLoading(false);
        } finally {
            streamActive = false;
            streamInFlightRef.current = false;
            clearTimeout(timeoutId); // 确保超时被清除
            setIsLoading(false);
            
            // 回答完毕后，自动继续录音（仅在移动端模式下，如果之前是在录音状态）
            if (wasRecording && mobileMode) {
                // 延迟1秒后自动继续录音，给用户一些时间查看回答
                setTimeout(() => {
                    // 确保当前没有在录音状态（使用ref检查，避免闭包问题）
                    if (!webAudioSpeechRecognizerRef.current) {
                        startVoiceRecognition();
                    }
                }, 1000);
            }
        }
    }, [chatLog, conversationCount, chatType, currentHostIP, mobileMode, autoPlayVoice]);

    const fetchRecommendedQuestions = async (message, selectedQuestion = null) => {
        const requestId = ++recommendationRequestRef.current;

        const length = message.length;
        const startIndex = Math.max(0, length - 10);
        const lastTenItems = message.slice(startIndex);

        // 清理消息，移除toolCalls和toolOutput
        const cleanLastTenItems = lastTenItems.map(msg => ({
            role: msg.role,
            content: msg.content
            // 不包含toolCalls和toolOutput
        }));

        setIsLoadingRecommendations(true); // 开始加载动画
        if (isStageProject && selectedQuestion) {
            setRecommendedQuestions(stageRecommendations.getQuestions(message, 3, selectedQuestion.index));
            setIsLoadingRecommendations(false);
            setTimeout(() => {
                endOfChatRef.current?.scrollIntoView({behavior: 'smooth'})
            }, 500);
            return;
        }
        const recommendUrl = currentHostIP + `/api/recommendQuestions`;
        const apiKey = '123456';
        try {
            const response = await axios.post(
                recommendUrl,
                {
                    history_messages: isStageProject ? message.map(({role, content}) => ({role, content})) : cleanLastTenItems,
                    vaccine_type: chatType,
                    ...(isStageProject ? {recommendation_mode: 'free_text'} : {})
                },
                {
                    ...(isStageProject ? {timeout: 20000} : {}),
                    headers: {
                        'Authorization': `${apiKey}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            const result = response.data.response;
            if (requestId !== recommendationRequestRef.current) return;
            if (isStageProject) {
                setRecommendedQuestions(stageRecommendations.completeFreeText(result, message));
            } else setRecommendedQuestions(result);
            setTimeout(() => {
                endOfChatRef.current?.scrollIntoView({behavior: 'smooth'})
            }, 500);
        } catch (error) {
            console.error('Error fetching recommended questions:', error);
            if (isStageProject && requestId === recommendationRequestRef.current) {
                setRecommendedQuestions(stageRecommendations.completeFreeText([], message));
            }
        } finally {
            if (requestId === recommendationRequestRef.current) setIsLoadingRecommendations(false); // 结束加载动画
        }
    };

    const fetchAnswerForQuestion = async (question) => {
        const recommendAnswerUrl = currentHostIP + `/api/recommendAnswer`;
        const apiKey = '123456';
        try {
            setIsInit(false);
            setDisplayedMessage('');
            let gptResponse;
            if (isStageProject) {
                const entry = stageRecommendations.getQuestion(question);
                if (!entry) throw new Error('推荐问题不在已核对的题库中');
                gptResponse = entry.answer;
            } else {
                const response = await axios.get(
                    recommendAnswerUrl,
                    {
                        params: {
                            answer_index: question.index,
                            vaccine_type: chatType
                        },
                        headers: {
                            'Authorization': `${apiKey}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                gptResponse = response.data.response;
            }
            const cleanGptResponse = sanitizeAssistantOutput(gptResponse);
            setChatLog(prevChatLog => {
                const newChatLog = [
                    ...prevChatLog,
                    {role: 'gpt', content: cleanGptResponse}
                ];
                // 如果自动朗读开启，立即开始播放（不需要等待显示完成）
                if ((autoPlayVoice && isDesktopBrowser) && cleanGptResponse && cleanGptResponse.trim()) {
                    // 延迟一小段时间，确保消息已添加到状态中
                    setTimeout(() => {
                        const messageIndex = newChatLog.length - 1;
                        startTTSPlayback(cleanGptResponse, messageIndex);
                    }, 100);
                }
                return newChatLog;
            });
            await Promise.all([
                displayMessageWithTypingEffect(cleanGptResponse),
                saveChatToServer([{role: 'user', content: question.question, ...(isStageProject ? {inputSource: 'recommendation', recommendationIndex: question.index} : {})}, {
                    role: 'gpt', content: cleanGptResponse
                }])
            ]);
            return {role: 'gpt', content: cleanGptResponse};
        } catch (error) {
            console.error('Error fetching answer:', error);
            addErrorBubble('获取推荐问题答案失败，请稍后重试。');
        }
    };

    const displayMessageWithTypingEffect = (fullMessage) => new Promise(resolve => {
        setDisplayedMessage('');
        let length = 0;
        const intervalId = setInterval(() => {
            length += 1;
            setDisplayedMessage(fullMessage.slice(0, length));
            endOfChatRef.current?.scrollIntoView({behavior: 'smooth'});
            if (length >= fullMessage.length) {
                clearInterval(intervalId);
                resolve();
            }
        }, 5);
    });

    useEffect(() => {
        endOfChatRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [chatLog]);

    const convertToLinks = (text) => {
        // 如果文本已经包含HTML标签，需要智能处理
        if (text.includes('<') && text.includes('>')) {
            // 使用正则表达式匹配HTML标签内的内容
            const htmlTagRegex = /<[^>]*>/g;
            const parts = text.split(htmlTagRegex);
            const tags = text.match(htmlTagRegex) || [];
            
            let result = '';
            let tagIndex = 0;
            
            for (let i = 0; i < parts.length; i++) {
                // 处理非HTML标签部分的文本
                if (parts[i]) {
                    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]*[-A-Za-z0-9+&@#/%?=~_|])(?![\w])/gi;
                    result += parts[i].replace(urlRegex, (url) => {
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
                    });
                }
                
                // 添加HTML标签（不处理标签内的内容）
                if (tagIndex < tags.length) {
                    result += tags[tagIndex];
                    tagIndex++;
                }
            }
            
            return result;
        }
        
        // 如果没有HTML标签，直接转换URL
        const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]*[-A-Za-z0-9+&@#/%?=~_|])(?![\w])/gi;
        return text.replace(urlRegex, (url) => {
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        });
    };

    const getDisplayContent = (content, role) => {
        return role === 'gpt' ? sanitizeAssistantOutput(content) : String(content || '');
    };

    const formatBubbleHtml = (content, role) => {
        const displayContent = getDisplayContent(content, role);
        const {protectedText, tokens} = preserveAllowedHtml(displayContent);
        const html = convertMarkdownBold(escapeHtml(protectedText));
        return convertToLinks(restoreAllowedHtml(html, tokens));
    };

    const handleRecommendedQuestionClick = async (questionObj) => {
        if (isLoading || recommendationInFlightRef.current) return;
        const question = isStageProject ? stageRecommendations.getQuestion(questionObj) : questionObj;
        if (!question) return;
        // 机构问题仍使用动态服务检索；价格与医保问题使用已核对的题库答案。
        if (chatType === 'RSV' && question.requiresServiceLookup) {
            await handleSendMessageStream(question.question, question);
            return;
        }
        recommendationInFlightRef.current = true;
        setIsLoading(true);
        setIsInit(false);
        setDisplayedMessage('');
        recommendationRequestRef.current += 1;
        const userMessage = {role: 'user', content: question.question, ...(isStageProject ? {inputSource: 'recommendation', recommendationIndex: question.index} : {})};
        const updatedChatLog = [...chatLog, userMessage];
        setChatLog(updatedChatLog);
        setConversationCount(count => count + 1);
        try {
            const answer = await fetchAnswerForQuestion(question);
            await fetchRecommendedQuestions(answer ? [...updatedChatLog, answer] : updatedChatLog, question);
        } finally {
            recommendationInFlightRef.current = false;
            setIsLoading(false);
        }
    };

    // 开始语音识别
    const startVoiceRecognition = async () => {

        if (!WebAudioSpeechRecognizer) {
            throw new Error('语音识别SDK未加载');
        }

        let credentials = tencentCredentials;
        console.log(tencentCredentials);
        if (!credentials) {
            credentials = await getTencentToken();
            if (!credentials) {
                return;
            }
        }

        setIsRecording(true);
        setRecordingStatus('connecting');
        setRecognizedText('');
        recordingStartTimeRef.current = Date.now(); // 记录录音开始时间

        const params = {
            // 使用临时密钥
            secretid: credentials.credentials.tmpSecretId,
            secretkey: credentials.credentials.tmpSecretKey,
            token: credentials.credentials.sessionToken,
            appid: credentials.appid,
            // 实时识别接口参数
            engine_model_type: '16k_zh', // 因为内置WebRecorder采样16k的数据，所以参数 engine_model_type 需要选择16k的引擎
            voice_format: 1,
            needvad: 1,
            filter_dirty: 1,
            filter_modal: 2,
            filter_punc: 0,
            convert_num_mode: 1,
        };

        try {
            const recognizer = new WebAudioSpeechRecognizer(params, true);
            webAudioSpeechRecognizerRef.current = recognizer;

            let resultText = '';
            let isCanStop = false;

            // 开始识别
            recognizer.OnRecognitionStart = (res) => {
                console.log('开始识别', res);
            };

            // 一句话开始
            recognizer.OnSentenceBegin = (res) => {
                console.log('一句话开始', res);
                isCanStop = true;
                setRecordingStatus('recording');
                setIsWaitingForPermission(false); // 权限已获取，开始录音
            };

            // 识别变化时
            recognizer.OnRecognitionResultChange = (res) => {
                console.log('识别变化时', res);
                const currentText = `${resultText}${res.result.voice_text_str}`;
                setRecognizedText(currentText);
                setMessage(currentText);
            };

            // 一句话结束
            recognizer.OnSentenceEnd = (res) => {
                console.log('一句话结束', res);
                resultText += res.result.voice_text_str;
                setRecognizedText(resultText);
                setMessage(resultText);
            };

            // 识别结束
            recognizer.OnRecognitionComplete = (res) => {
                console.log('识别结束', res);
                setRecordingStatus('');
                resultText = '';
            };

            // 识别错误
            recognizer.OnError = (res) => {
                console.log('识别失败', res);

                // 检查是否是短录音导致的错误，如果是则静默处理
                const recordingDuration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;
                const minRecordingTime = 300; // 最小录音时间300ms
                if (recordingDuration < minRecordingTime) {
                    console.log('录音时间太短，忽略错误提示');
                } else {
                    addErrorBubble('语音识别失败，请重试。');
                }

                // 确保释放麦克风资源
                if (webAudioSpeechRecognizerRef.current) {
                    webAudioSpeechRecognizerRef.current.destroyStream();
                    webAudioSpeechRecognizerRef.current = null;
                }
                setIsRecording(false);
                setRecordingStatus('');
                setRecognizedText('');
                setIsWaitingForPermission(false); // 权限处理完成
                setShowVoiceOverlay(false); // 隐藏语音浮层
            };

            // 录音停止时
            recognizer.OnRecorderStop = (res) => {
                console.log('录音停止', res);
                resultText = '';
            };

            recognizer.start();
        } catch (error) {
            console.error('启动语音识别失败:', error);
            addErrorBubble('启动语音识别失败，请重试。');
            // 确保释放麦克风资源
            if (webAudioSpeechRecognizerRef.current) {
                webAudioSpeechRecognizerRef.current.destroyStream();
                webAudioSpeechRecognizerRef.current = null;
            }
            setIsRecording(false);
            setRecordingStatus('');
            setRecognizedText('');
            setIsWaitingForPermission(false); // 权限处理完成
            setShowVoiceOverlay(false); // 隐藏语音浮层
        }
    };

    // 停止语音识别
    const stopVoiceRecognition = useCallback(() => {
        if (webAudioSpeechRecognizerRef.current) {
            webAudioSpeechRecognizerRef.current.stop();
            // 释放麦克风资源
            webAudioSpeechRecognizerRef.current.destroyStream();
            webAudioSpeechRecognizerRef.current = null;
        }
        setIsRecording(false);
        setRecordingStatus('');
        setRecognizedText('');
        recordingStartTimeRef.current = null; // 重置录音开始时间
    }, []);

    // 开始TTS播放
    const startTTSPlayback = async (messageContent, messageIndex) => {
        if (!WebAudioTTS) {
            throw new Error('TTS SDK未加载');
        }

        let credentials = tencentCredentials;
        if (!credentials) {
            credentials = await getTencentToken();
            if (!credentials) {
                return;
            }
        }

        // 停止当前播放
        stopTTSPlayback();

        setIsPlaying(false);
        setIsSynthesizing(true);
        setCurrentPlayingMessage(messageIndex);
        setPlaybackProgress(0);
        setCurrentTime(0);
        setTotalDuration(0);

        const params = {
            secretid: credentials.credentials.tmpSecretId,
            secretkey: credentials.credentials.tmpSecretKey,
            token: credentials.credentials.sessionToken,
            appid: credentials.appid,
            voice_type: 502003, // 默认音色
            speed: 0, // 默认语速
            volume: 0, // 默认音量
        };

        try {
            const tts = new WebAudioTTS(currentHostIP,params, true);
            webAudioTTSRef.current = tts;

            // 设置合成开始回调
            tts.OnSynthesisStart = () => {
                setIsSynthesizing(true);
            };

            // 设置进度更新回调
            tts.OnProgressUpdate = (progress, currentTimeValue, durationValue) => {
                setPlaybackProgress(progress);
                setCurrentTime(currentTimeValue || 0);
                setTotalDuration(durationValue || 0);
            };

            // 设置时长加载完成回调
            tts.OnDurationLoaded = (duration) => {
                setTotalDuration(duration || 0);
            };

            // 设置播放开始回调
            tts.OnPlaybackStart = () => {
                setIsSynthesizing(false);
                setIsPlaying(true);
            };

            // 设置播放完成回调
            tts.OnPlaybackComplete = () => {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingMessage(null);
                setPlaybackProgress(0);
                setCurrentTime(0);
                setTotalDuration(0);
            };

            // 设置播放停止回调
            tts.OnPlaybackStopped = () => {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingMessage(null);
                setPlaybackProgress(0);
                setCurrentTime(0);
                setTotalDuration(0);
            };

            // 清理文本，移除参考文献部分和"信息来源"及其后面的内容
            let cleanText = messageContent.replace(/\[\d+\]/g, '').trim();
            
            // 移除"信息来源"及其后面的所有内容
            const infoSourceIndex = cleanText.indexOf('信息来源');
            if (infoSourceIndex !== -1) {
                cleanText = cleanText.substring(0, infoSourceIndex).trim();
            }
            
            if (!cleanText) {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingMessage(null);
                setPlaybackProgress(0);
                setCurrentTime(0);
                setTotalDuration(0);
                return;
            }

            await tts.start(cleanText);

        } catch (error) {
            console.error('启动TTS失败:', error);
            addErrorBubble('启动语音合成失败，请重试。');
            setIsPlaying(false);
            setIsSynthesizing(false);
            setCurrentPlayingMessage(null);
            setPlaybackProgress(0);
            setCurrentTime(0);
            setTotalDuration(0);
        }
    };

    // 停止TTS播放
    const stopTTSPlayback = () => {
        if (webAudioTTSRef.current) {
            webAudioTTSRef.current.stop();
            webAudioTTSRef.current = null;
        }
        setIsPlaying(false);
        setIsSynthesizing(false);
        setCurrentPlayingMessage(null);
        setPlaybackProgress(0);
        setCurrentTime(0);
        setTotalDuration(0);
    };
    
    // 格式化时间显示（秒转 mm:ss）
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    
    // 处理消息气泡点击（但链接优先）
    const handleMessageBubbleClick = (e, messageContent, messageIndex) => {
        // 如果点击的是链接，不触发播放
        if (e.target.tagName === 'A' || e.target.closest('a')) {
            return;
        }
        
        // 如果点击的是工具调用组件，不触发播放
        if (e.target.closest(`.${styles.tool_call_container}`)) {
            return;
        }
        
        // 如果点击的是推荐问题，不触发播放
        if (e.target.closest(`.${styles.recommended_questions}`)) {
            return;
        }
        
        // 如果点击的是进度条区域，不触发播放/暂停
        if (e.target.closest(`.${styles.progress_bar_container}`)) {
            return;
        }
        
        // 切换播放状态
        // 如果正在生成语音或正在播放，点击可以取消
        if (currentPlayingMessage === messageIndex && (isPlaying || isSynthesizing)) {
            stopTTSPlayback();
        } else {
            startTTSPlayback(messageContent, messageIndex);
        }
    };
    
    // 处理进度条点击和拖动
    const [isDraggingProgress, setIsDraggingProgress] = useState(false);
    const progressBarRefs = useRef({});
    
    // 计算点击位置对应的播放进度
    const getProgressFromEvent = (e, container) => {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const progress = Math.max(0, Math.min(1, x / rect.width));
        return progress;
    };
    
    // 处理进度条点击
    const handleProgressBarClick = (e, messageIndex) => {
        if (!isPlaying || currentPlayingMessage !== messageIndex) return;
        
        e.stopPropagation(); // 阻止冒泡到气泡
        
        const progress = getProgressFromEvent(e, e.currentTarget);
        if (webAudioTTSRef.current) {
            webAudioTTSRef.current.setProgress(progress);
        }
    };
    
    // 处理进度条拖动开始
    const handleProgressBarMouseDown = (e, messageIndex) => {
        if (!isPlaying || currentPlayingMessage !== messageIndex) return;
        
        e.stopPropagation(); // 阻止冒泡到气泡
        e.preventDefault();
        
        setIsDraggingProgress(true);
        
        const progress = getProgressFromEvent(e, e.currentTarget);
        if (webAudioTTSRef.current) {
            webAudioTTSRef.current.setProgress(progress);
        }
    };
    
    // 处理进度条拖动
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingProgress || !isPlaying || currentPlayingMessage === null) return;
            
            const container = progressBarRefs.current[currentPlayingMessage];
            if (container) {
                const progress = getProgressFromEvent(e, container);
                
                if (webAudioTTSRef.current) {
                    webAudioTTSRef.current.setProgress(progress);
                }
            }
        };
        
        const handleMouseUp = () => {
            setIsDraggingProgress(false);
        };
        
        if (isDraggingProgress) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDraggingProgress, isPlaying, currentPlayingMessage]);

    // 处理语音识别指南导航
    const handleGuideNext = () => {
        if (currentGuideStep < 4) {
            // 在进入第四步前取消高亮（从第三步进入第四步）
            if (currentGuideStep === 3) {
                setHighlightLastBubble(false);
            }
            setCurrentGuideStep(currentGuideStep + 1);
            // 在进入第三步时高亮最后一个assistant气泡
            if (currentGuideStep === 2) {
                setHighlightLastBubble(true);
            }
        }
    };

    const handleGuidePrev = () => {
        if (currentGuideStep > 1) {
            setCurrentGuideStep(currentGuideStep - 1);
            // 回到第二步时取消高亮（从第三步回到第二步）
            if (currentGuideStep === 3) {
                setHighlightLastBubble(false);
            }
            // 从第四步回到第三步时设置高亮
            if (currentGuideStep === 4) {
                setHighlightLastBubble(true);
            }
        }
    };

    const handleGuideFinish = () => {
        // 关闭提示框并重置步骤和高亮状态
        setShowVoiceGuide(false);
        setCurrentGuideStep(1);
        setHighlightLastBubble(false);
    };

    const handleGuideSkip = () => {
        // 跳过引导，设置localStorage标记并关闭
        localStorage.setItem('hasShownVoiceGuide', 'true');
        setShowVoiceGuide(false);
        setCurrentGuideStep(1);
        setHighlightLastBubble(false);
        hasCheckedGuide.current = true;
    };

    
    // 移动端按住录音开始
    const handleTouchStart = (e) => {
        // 如果强制桌面模式（微信浏览器选择仍要使用），禁用按住说话
        if (forceDesktopMode) return;
        if (!mobileMode || inputMode !== 'voice' || isLoading) return;

        e.preventDefault(); // 阻止默认行为，防止文字选择
        e.stopPropagation();
        const touch = e.touches[0];
        setTouchStartY(touch.clientY);
        setCurrentTouchY(touch.clientY);
        setIsDragging(false);
        setShowVoiceOverlay(true);
        setIsWaitingForPermission(true); // 标记正在等待权限

        // 开始录音
        startVoiceRecognition();
    };

    // 移动端拖动
    const handleTouchMove = (e) => {
        if (!mobileMode || inputMode !== 'voice' || !isRecording) return;
        
        e.preventDefault(); // 阻止默认行为，防止文字选择
        e.stopPropagation();
        
        const touch = e.touches[0];
        const currentY = touch.clientY;
        setCurrentTouchY(currentY);
        
        // 判断是否向上拖动超过阈值（例如100px）
        const dragDistance = touchStartY - currentY;
        if (dragDistance > 100) {
            setIsDragging(true);
        } else {
            setIsDragging(false);
        }
    };

    // 移动端松手
    const handleTouchEnd = (e) => {
        if (!mobileMode || inputMode !== 'voice') return;

        e.preventDefault(); // 阻止默认行为
        e.stopPropagation(); // 阻止事件冒泡
        setShowVoiceOverlay(false);
        setIsWaitingForPermission(false); // 重置等待权限状态

        // 如果正在等待权限，但还没有开始录音，不需要停止
        if (isWaitingForPermission && !isRecording) return;

        if (!isRecording) return;

        // 检查录音时长，如果太短则忽略（避免快速点击导致的识别失败）
        const recordingDuration = Date.now() - recordingStartTimeRef.current;
        const minRecordingTime = 300; // 最小录音时间300ms
        if (recordingDuration < minRecordingTime) {
            // 录音时间太短，直接停止并清空，不显示错误
            stopVoiceRecognition();
            return;
        }

        // 停止录音
        stopVoiceRecognition();

        // 判断是编辑还是直接发送
        if (isDragging) {
            // 切换到文字输入模式，保留识别的文字
            setInputMode('text');
            setIsDragging(false);
        } else {
            // 直接发送
            if (recognizedText && recognizedText.trim()) {
                handleSendMessageStream(recognizedText);
                setRecognizedText('');
                setMessage('');
            }
        }
    };

    // 切换输入模式
    const toggleInputMode = () => {
        if (inputMode === 'text') {
            setInputMode('voice');
        } else {
            setInputMode('text');
        }
    };

    // 处理输入框内容变化和高度调整
    const handleInputChange = (e) => {
        const value = e.target.value;
        setMessage(value);

        // 动态调整高度
        const textarea = e.target;
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.min(Math.max(scrollHeight, 40), 120); // 最小40px，最大120px
        textarea.style.height = newHeight + 'px';
        setInputHeight(newHeight);
    };

    // 处理键盘事件
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
            e.preventDefault();
            handleSendMessageStream(message);
        }
    };

    // 获取箭头位置（使用屏幕百分比）
    const getArrowPosition = (step, shouldForceDesktop) => {
        switch (step) {
            case 1:
                // 指向必备知识按钮 - 右上角区域
                return { top: '20%', left: '80%', direction: 'left' };

            case 2:
                // 指向语音输入框 - 底部中央
                return { top: '80%', left: '45%', direction: 'up' };

            case 3:
                // 第三步不需要箭头
                return null;

            default:
                return null; // 第四步不需要箭头
        }
    };

    // 根据当前步骤获取元素z-index
    const getElementZIndex = (elementType) => {
        if (!showVoiceGuide) return undefined;

        // 只有当前步骤中要介绍的元素才设置高z-index
        if (currentGuideStep === 1 && elementType === 'dropdown') {
            return '10003';
        }
        if (currentGuideStep === 2 && elementType === 'input') {
            return '10003';
        }
        if (currentGuideStep === 3 && elementType === 'lastBubble') {
            return '10003';
        }

        return undefined;
    };

    return (
        <div className={`${styles.parent} ${chatType === 'HPV' ? styles.hpvChat : ''}`}>
            {/* 语音识别使用指南 */}
            {showVoiceGuide && (
                <>
                    {/* 灰色遮罩层 - 独立元素 */}
                    <div className={styles.voice_guide_overlay}></div>

                    {/* 动态箭头 - 在所有元素之上 */}
                    {(() => {
                        const arrowPos = getArrowPosition(currentGuideStep, forceDesktopMode);
                        if (!arrowPos) return null;

                        let arrowIcon = '';

                        switch (currentGuideStep) {
                            case 1:
                                arrowIcon = '↗';
                                break;
                            case 2:
                                arrowIcon = '⬇';
                                break;
                            case 3:
                                arrowIcon = '⬇';
                                break;
                        }

                        if (!arrowIcon) return null;

                        return (
                            <div
                                className={styles.dynamic_arrow}
                                style={{
                                    position: 'fixed',
                                    top: arrowPos.top,
                                    left: arrowPos.left,
                                    zIndex: 10004,
                                    animation: 'arrowIconBounce 1.5s infinite'
                                }}
                            >
                                <div className={styles.arrow_icon}>{arrowIcon}</div>
                            </div>
                        );
                    })()}

                    {/* 窗口内容 - 独立元素 */}
                    <div className={`${styles.voice_guide_modal_container} ${currentGuideStep === 1 ? styles.modal_right : currentGuideStep === 2 ? styles.modal_top : currentGuideStep === 3 ? styles.modal_right : ''}`}>
                        <div className={styles.voice_guide_modal}>
                            {/* 步骤指示器 */}
                            <div className={styles.step_indicator}>
                                <div className={`${styles.step_dot} ${currentGuideStep >= 1 ? styles.active : ''}`}></div>
                                <div className={`${styles.step_dot} ${currentGuideStep >= 2 ? styles.active : ''}`}></div>
                                <div className={`${styles.step_dot} ${currentGuideStep >= 3 ? styles.active : ''}`}></div>
                                <div className={`${styles.step_dot} ${currentGuideStep >= 4 ? styles.active : ''}`}></div>
                            </div>

                            <div className={styles.voice_guide_content}>
                                <div className={styles.voice_guide_title}>
                                    使用指南
                                </div>

                                {/* 动态显示当前步骤内容 */}
                                {currentGuideStep === 1 && (
                                    <div className={styles.guide_step}>
                                        <div className={styles.step_number}>1</div>
                                        <div className={styles.step_content}>
                                            <div className={styles.step_title}>查看必备知识</div>
                                            <div className={styles.step_description}>
                                                点击右上角的"必备知识"按钮，了解{chatType === 'HPV' ? 'HPV疫苗' : chatType === 'RSV' ? 'RSV预防' : chatType === 'CRC' || chatType === '肿瘤' || chatType === '癌症' ? '肠癌筛查' : '流感疫苗'}的相关信息
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {currentGuideStep === 2 && (
                                    <div className={styles.guide_step}>
                                        <div className={styles.step_number}>2</div>
                                        <div className={styles.step_content}>
                                            <div className={styles.step_title}>使用语音识别</div>
                                            <div className={styles.step_description}>
                                                {isWechatBrowser ? (
                                                    <>点击录音机按钮开始录音，再次点击结束录音并发送</>
                                                ) : isDesktopBrowser ? (
                                                    <>点击语音按钮开始录音，再次点击结束录音并发送</>
                                                ) : (
                                                    <>按住语音按钮开始录音，松手自动发送</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {currentGuideStep === 3 && (
                                    <div className={styles.guide_step}>
                                        <div className={styles.step_number}>3</div>
                                        <div className={styles.step_content}>
                                            <div className={styles.step_title}>体验语音播放</div>
                                            <div className={styles.step_description}>
                                                点击医生回复可以播放语音解答
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {currentGuideStep === 4 && (
                                    <div className={styles.guide_step}>
                                        <div className={styles.step_number}>4</div>
                                        <div className={styles.step_content}>
                                            <div className={styles.step_title}>开始提问</div>
                                            <div className={styles.step_description}>
                                                请至少问10个问题来获得完整的解答体验
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 导航按钮 */}
                            <div className={styles.voice_guide_buttons}>
                                {currentGuideStep > 1 && (
                                    <button
                                        className={styles.voice_guide_prev_button}
                                        onClick={handleGuidePrev}
                                    >
                                        上一步
                                    </button>
                                )}
                                {currentGuideStep < 4 ? (
                                    <button
                                        className={styles.voice_guide_next_button}
                                        onClick={handleGuideNext}
                                    >
                                        下一步
                                    </button>
                                ) : (
                                    <button
                                        className={styles.voice_guide_finish_button}
                                        onClick={handleGuideFinish}
                                    >
                                        我知道了
                                    </button>
                                )}
                                <button
                                    className={styles.voice_guide_skip_button}
                                    onClick={handleGuideSkip}
                                >
                                    跳过引导
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            
            <div className={styles.header_group}>
                <h1 className={styles.chat_title}>
                    {chatType === 'CRC' || chatType === '肿瘤' || chatType === '癌症' || chatType === '肠癌' ? '肠癌筛查你问我答' :
                     chatType === 'HPV' ? 'HPV疫苗你问我答' :
                     chatType === 'RSV' ? 'RSV预防你问我答' : '流感疫苗你问我答'}
                </h1>
            </div>
            <div style={{zIndex: getElementZIndex('dropdown')}}>
                <ExpandableDropdown chatType={chatType} wechatWarningVisible={showVoiceGuide}/>
            </div>
            <div className={styles.chat_container}>
                <div id="chat-log" className={styles.chat_log}>
                    {chatLog.map((message, index) => (
                        <div className="historyWrapper">
                            <div key={index}
                                 className={`${styles.message_row} ${message.role === 'user' ? styles.user : message.role === 'gpt' ? styles.gpt : styles.system}`}>
                                {message.role === 'gpt' ? (
                                    <>
                                        <Avatar role={message.role} chatType={chatType}/>
                                        <div className={styles.single_ans_item}>
                                            {/* 显示工具调用 */}
                                            {message.toolCalls && message.toolCalls.length > 0 && (
                                                <div className={styles.tool_calls_container}>
                                                    {message.toolCalls.map((toolCallData, toolIndex) => (
                                                        <ToolCallComponent
                                                            key={toolCallData.uuid || `${index}-${toolIndex}`}
                                                            toolCall={toolCallData.toolCall}
                                                            toolOutput={toolCallData.toolOutput}
                                                            uuid={toolCallData.uuid || `${index}-${toolIndex}`}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            {/* 显示文本内容 */}
                                            {message.content && (() => {
                                                const isStreamingLastMessage = message.role === 'gpt' && message.content === chatLog[chatLog.length - 1].content && index === chatLog.length - 1 && !isInit;
                                                const visibleContent = message.clinicRecommendation?.vaccine_type === 'RSV'
                                                    ? getDisplayContent(locatedServiceReply(message.clinicRecommendation), message.role)
                                                    : isStreamingLastMessage
                                                    ? getDisplayContent(displayedMessage, message.role)
                                                    : getDisplayContent(message.content, message.role);

                                                return (
                                                    <div className={styles.message_content_container} style={{zIndex: highlightLastBubble && index === lastAssistantIndex ? '10002' : 'auto'}}>
                                                        <div
                                                            className={`${styles.message_bubble} ${styles.gpt_message} ${currentPlayingMessage === index ? styles.message_bubble_playing : ''} ${isSynthesizing && currentPlayingMessage === index ? styles.message_bubble_synthesizing : ''} ${highlightLastBubble && index === lastAssistantIndex ? styles.message_bubble_highlighted : ''}`}
                                                            onClick={(e) => handleMessageBubbleClick(e, visibleContent, index)}
                                                            style={{ cursor: 'pointer', position: 'relative' }}
                                                            onMouseEnter={(e) => {
                                                                if (currentPlayingMessage !== index || (!isPlaying && !isSynthesizing)) {
                                                                    e.currentTarget.setAttribute('data-show-hint', 'true');
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.removeAttribute('data-show-hint');
                                                            }}
                                                        >
                                                        {/* 未播放时的悬浮提示 */}
                                                        {(!isPlaying && !isSynthesizing) && (
                                                            <div className={`${styles.play_hint_tooltip} ${index === 0 ? styles.play_hint_tooltip_bottom : ''}`}>
                                                                点击播放语音
                                                            </div>
                                                        )}
                                                        {/* 合成中动画 */}
                                                        {isSynthesizing && currentPlayingMessage === index && (
                                                            <div className={styles.synthesizing_indicator}>
                                                                <span className={styles.synthesizing_text}>正在生成语音...</span>
                                                                <div className={styles.synthesizing_dots}>
                                                                    <span></span>
                                                                    <span></span>
                                                                    <span></span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* 进度条（带时间显示） */}
                                                        {currentPlayingMessage === index && !isSynthesizing && (
                                                            <div 
                                                                ref={(el) => {
                                                                    if (el) {
                                                                        progressBarRefs.current[index] = el;
                                                                    } else {
                                                                        delete progressBarRefs.current[index];
                                                                    }
                                                                }}
                                                                className={`${styles.progress_bar_container} ${isDraggingProgress ? styles.progress_bar_dragging : ''}`}
                                                                onClick={(e) => handleProgressBarClick(e, index)}
                                                                onMouseDown={(e) => handleProgressBarMouseDown(e, index)}
                                                                style={{ cursor: isPlaying ? 'pointer' : 'default' }}
                                                            >
                                                                <div 
                                                                    className={styles.progress_bar}
                                                                    style={{ width: `${playbackProgress}%` }}
                                                                />
                                                                <div className={styles.progress_time_display}>
                                                                    {formatTime(currentTime)} / {formatTime(totalDuration)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        <div className={styles.message_content_text}
                                                            dangerouslySetInnerHTML={{
                                                                __html: formatBubbleHtml(visibleContent, message.role)
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                );
                                            })()}

                                            <MiDebugPanel enabled={debugMode} data={message.miState} previousData={previousMiDebugStates[index]}/>

                                            {messageServiceResults(message).map((result, resultIndex) => (
                                                <ServiceResultCard key={resultIndex} data={result}/>
                                            ))}
                                            {message.clinicRecommendation && (
                                                <ClinicRecommendationCard data={message.clinicRecommendation}
                                                    onRetry={isLoading ? null : () => handleSendMessageStream(
                                                        message.clinicRecommendation.query_type === 'pilot_status'
                                                            ? '请重新定位并查询附近的RCT试点' : '请重新定位并查询附近的RSV接种机构'
                                                    )}/>
                                            )}

                                            {!isLoading && index === chatLog.length - 1 && recommendedQuestions.length > 0 && (
                                                <div className={styles.recommended_bubble_container}>
                                                    <div className={styles.recommended_questions}>
                                                        {isLoadingRecommendations ? (
                                                            <div
                                                                className={styles.loading_animation}>加载推荐问题中...</div>
                                                        ) : (
                                                            <ul>
                                                                {recommendedQuestions.map((questionObj, qIndex) => (
                                                                    <li key={qIndex}
                                                                        onClick={() => handleRecommendedQuestionClick(questionObj)}>
                                                                        {questionObj.question}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : message.role === 'user' ? (
                                    <>
                                        <div
                                            className={`${styles.message_bubble} ${styles.user_message}`}
                                            dangerouslySetInnerHTML={{__html: formatBubbleHtml(message.content, message.role)}}
                                        />
                                        <Avatar role={message.role} gender={gender} chatType={chatType}/>
                                    </>
                                ) : (
                                    <div className={styles.error_bubble}>
                                        {message.content}
                                    </div>
                                )}

                            </div>
                            {index === historyLength - 1 && historyLength > 0 && (
                                <div className={styles.history_notice}>
                                    <span className={styles.line}></span>
                                    以上是回溯的历史记录
                                    <span className={styles.line}></span>
                                </div>
                            )}
                            {chatType === 'HPV' && (
                                (historyLength > 0 && index === chatLog.length - 1)
                            ) && (
                                <div className={styles.hpv_subsidy_progress}>
                                    {hpvCompletedRounds >= HPV_TARGET_CONVERSATIONS ? (
                                        <>
                                            已完成 {hpvCompletedRounds} 次问答，
                                            <button
                                                type="button"
                                                className={styles.hpv_subsidy_trigger}
                                                onClick={() => setShowHpvSubsidyModal(true)}
                                            >领取红包</button>
                                        </>
                                    ) : (
                                        <>还需聊天 {getRemainingHpvConversations(hpvCompletedRounds)} 次，即可领取红包。</>
                                    )}
                                </div>
                            )}
                            {/* 在历史记录后或只有初始消息时显示自动朗读提示 */}
                            {((index === historyLength - 1 && historyLength > 0) || (historyLength === 0 && index === chatLog.length - 1)) && (!autoPlayVoice || !isDesktopBrowser) && (
                                <div className={styles.auto_play_notice}>
                                    ⚠️ 自动朗读已关闭，点击医生回复可播放语音
                                </div>
                            )}


                        </div>
                    ))}

                    {isLoading && <div className={styles.loading_animation}>正在思考...</div>}
                    <div ref={endOfChatRef}/>
                </div>


            </div>
            {showHpvSubsidyModal && (
                <HpvSubsidyModal onClose={() => setShowHpvSubsidyModal(false)}/>
            )}
            
            {/* 移动端语音识别浮层 */}
            {mobileMode && showVoiceOverlay && (
                <div 
                    className={styles.voice_overlay}
                    onTouchStart={(e) => e.preventDefault()}
                    onTouchMove={(e) => e.preventDefault()}
                    onTouchEnd={(e) => e.preventDefault()}
                >
                    <div className={`${styles.voice_overlay_content} ${isDragging ? styles.dragging : ''}`}>
                        <div className={styles.voice_text_display}>
                            {recognizedText || '请说话...'}
                        </div>
                        <div className={styles.voice_hint}>
                            {isDragging ? (
                                <div className={styles.edit_zone}>
                                    <span className={styles.edit_icon}>✏️</span>
                                    <span>松手编辑文字</span>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.recording_indicator}>
                                        <span className={styles.recording_dot}>●</span>
                                        正在录音...
                                    </div>
                                    <div className={styles.drag_hint}>↑ 向上滑动编辑文字</div>
                                    <div className={styles.release_hint}>松手直接发送</div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.input_container} style={{zIndex: getElementZIndex('input')}}>
                {/* 移动端语音按钮区域 - 放在输入框上方 */}
                {mobileMode && (
                    <div className={styles.voice_controls_row}>
                        {/* 移动端模式切换按钮（微信浏览器强制桌面模式时禁用） */}
                        {!forceDesktopMode && (
                            <button
                                onClick={toggleInputMode}
                                className={styles.mode_toggle_button}
                                disabled={isLoading || isRecording || isWaitingForPermission}
                                title={inputMode === 'text' ? '切换到语音输入' : '切换到文字输入'}
                            >
                                {inputMode === 'text' ? '🎤' : '⌨️'}
                            </button>
                        )}

                        {/* 微信浏览器显示录音机按钮 */}
                        {isWechatBrowser && (
                            <button
                                onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
                                className={`${styles.voice_button} ${isRecording ? styles.voice_button_active : ''}`}
                                disabled={isLoading || isWaitingForPermission}
                                title={isRecording ? '点击停止录音' : '点击开始语音输入'}
                            >
                                <span className={styles.voice_button_icon}>
                                    {recordingStatus === 'connecting' && '🔌'}
                                    {recordingStatus === 'recording' && '🎙️'}
                                    {!recordingStatus && '🎙️'}
                                </span>
                                <span className={styles.voice_button_text}>
                                    {isRecording ? '停止录音' : '开始录音'}
                                </span>
                            </button>
                        )}

                        {/* 移动端语音模式显示按住录音按钮（仅在非微信浏览器时显示） */}
                        {inputMode === 'voice' && !isWechatBrowser && !forceDesktopMode && (
                            <button
                                ref={voiceButtonRef}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onContextMenu={(e) => e.preventDefault()} // 阻止长按菜单
                                onSelectStart={(e) => e.preventDefault()} // 阻止文字选择开始
                                className={`${styles.hold_voice_button} ${isRecording ? styles.hold_voice_button_active : ''}`}
                                disabled={isLoading}
                            >
                                {isRecording ? '松手发送' : '按住说话'}
                            </button>
                        )}
                    </div>
                )}

                {/* PC端录音按钮 - 保持原有位置 */}
                {!mobileMode && (
                    <button
                        onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
                        className={`${styles.voice_button} ${isRecording ? styles.voice_button_active : ''}`}
                        disabled={isLoading || isWaitingForPermission}
                        title={isRecording ? '点击停止录音' : '点击开始语音输入'}
                    >
                        <span className={styles.voice_button_icon}>
                            {recordingStatus === 'connecting' && '🔌'}
                            {recordingStatus === 'recording' && '🎙️'}
                            {!recordingStatus && '🎙️'}
                        </span>
                        <span className={styles.voice_button_text}>
                            {isRecording ? '停止录音' : '开始录音'}
                        </span>
                    </button>
                )}

                {/* 多行文字输入框 */}
                {(!mobileMode || inputMode === 'text') && (
                    <div className={styles.input_wrapper} style={{height: inputHeight + 16}}>
                        <textarea
                            value={message}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            className={styles.chat_textarea}
                            placeholder={isRecording ? '正在录音...' : mobileMode ? '输入一切你想问的...' : '输入一切你想问的...\n(按Enter发送，Shift+Enter换行)'}
                            disabled={isLoading}
                            rows={1}
                            style={{height: inputHeight}}
                        />
                        <button
                            onClick={() => handleSendMessageStream(message)}
                            className={styles.send_button}
                            disabled={isLoading}
                        >
                            发送
                        </button>
                    </div>
                )}
            </div>

            {/*/!* 视频咨询悬浮按钮 *!/*/}
            {/*<button*/}
            {/*    className={styles.videoConsultButton}*/}
            {/*    onClick={() => {*/}
            {/*    }}*/}
            {/*    title="视频咨询"*/}
            {/*>*/}
            {/*    <img src="/视频咨询按钮.png" alt="视频咨询" />*/}
            {/*</button>*/}

        </div>
    );

};

export default GptChat;
